import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Inventory as InventoryIcon,
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { supabase } from '../utils/supabase';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();

    // Listen for auth changes (including profile updates)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
          // Refresh user data when auth state changes
          const { data: { user } } = await supabase.auth.getUser();
          setUser(user);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    // Listen for custom profile update events from SettingsPage
    const handleProfileUpdate = async (event) => {
      console.log('🔄 [Navbar] Profile update event received:', event.detail);
      
      // Small delay to ensure Supabase has processed the update
      setTimeout(async () => {
        try {
          // Refresh user data to get the latest profile information
          const { data: { user } } = await supabase.auth.getUser();
          setUser(user);
          console.log('🔄 [Navbar] User data refreshed:', {
            displayName: user?.user_metadata?.display_name,
            username: user?.user_metadata?.username,
            email: user?.email
          });
        } catch (error) {
          console.error('🔄 [Navbar] Error refreshing user data:', error);
        }
      }, 500); // 500ms delay to ensure auth update is complete
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    // Cleanup subscription and event listener on unmount
    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error logging out:', error);
    } else {
      navigate('/login');
    }
    setAnchorEl(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navigateToSettings = () => {
    navigate('/settings');
    setAnchorEl(null);
  };

  const navItems = [
    { label: 'Home', path: '/', key: 'home', icon: <HomeIcon /> },
    { label: 'Pantry', path: '/pantry', key: 'pantry', icon: <InventoryIcon /> },
    { label: 'Generate Recipe', path: '/generate-recipe', key: 'generate', icon: <RestaurantIcon /> },
    { label: 'Cookbook', path: '/cookbook', key: 'cookbook', icon: <MenuBookIcon /> }
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar position="sticky" elevation={2}>
        <Toolbar>
          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo and Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img 
              src="/logo-long.jpg" 
              alt="Virtual Kitchen Logo" 
              style={{ 
                width: 250, 
                height: 48, 
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* Desktop Navigation Links */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
            {navItems.map((item) => (
              <Button
                key={item.key}
                color="inherit"
                onClick={() => navigate(item.path)}
                sx={{
                  fontWeight: isActivePath(item.path) ? 'bold' : 'normal',
                  borderBottom: isActivePath(item.path) ? 2 : 0,
                  borderBottomColor: 'primary.contrastText',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Spacer for mobile */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', sm: 'none' } }} />

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {user && (
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user.user_metadata?.display_name || user.user_metadata?.username || user.email}
              </Typography>
            )}
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{ mt: 1.5 }}
            >
              <MenuItem onClick={navigateToSettings}>
                <SettingsIcon sx={{ mr: 1 }} />
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/logo-long.jpg" 
                alt="Virtual Kitchen Logo" 
                style={{ 
                  width: 250, 
                  height: 40, 
                  objectFit: 'contain'
                }}
              />
            </Box>
            {user && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                {user.user_metadata?.display_name || user.user_metadata?.username || user.email}
              </Typography>
            )}
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton 
                  onClick={() => handleMobileNavClick(item.path)}
                  selected={isActivePath(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActivePath(item.path) ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontWeight: isActivePath(item.path) ? 'bold' : 'normal',
                        color: isActivePath(item.path) ? 'primary.main' : 'inherit'
                      } 
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={navigateToSettings}>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
